import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { DeviceManagerService } from '@app/core/services/device-manager.service';
import { ErrorHandlerService } from '@app/core/services';
import { ICentralUserInfo } from '@app/shared/models';
import { routerTransition } from '@app/shared/utils/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserProfileManagerService } from '@app/api/user-profile/user-profile-manager.service';
import { HeaderComponent } from '@app/shared/layout/header/header.component';
import { SidebarComponent } from '@app/shared/layout/sidebar/sidebar.component';
import { FeedbackPromptComponent } from '@app/shared/layout/feedback-prompt/feedback-prompt.component';
import { FeedbackWidgetComponent } from '@app/shared/layout/feedback-widget/feedback-widget.component';
import { environment } from '@env/environment';

@Component({
    selector: 'app-main-layout',
    templateUrl: './main-layout.component.html',
    styleUrls: ['./main-layout.component.scss'],
    animations: [routerTransition],
    // OnPush (AB#24384). This view hosts the <router-outlet>, so every routed page is below it. The app is zoneless and
    // every page holds its template state in signals (AB#23266), so a page redraws itself when its data arrives. An
    // Eager layout would also re-check every Eager page whenever the layout refreshed, which hid pages that were not
    // signal-safe; AB#24308 was the opposite case, an OnPush layout over plain-field pages under zone.js.
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [HeaderComponent, SidebarComponent, RouterOutlet, FeedbackPromptComponent, FeedbackWidgetComponent]
})
export class MainLayoutComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    private deviceManager = inject(DeviceManagerService);
    private errorHandler = inject(ErrorHandlerService);
    private route = inject(ActivatedRoute);
    private userProfileManager = inject(UserProfileManagerService);

    // Signals, so the shell re-renders when they change without zone.js (ADR 0008, AB#23266).
    protected readonly isPda = signal(false);
    protected readonly isSmallDesktop = signal(false);
    protected readonly panelPinned = signal(false);
    protected readonly panelOpen = signal(false);
    // Undefined until the profile call returns; the header and sidebar take it as an input.
    protected readonly currentUser = signal<ICentralUserInfo | undefined>(undefined);
    /** The profile read failed, so currentUser will stay undefined; the header stops waiting on it (ER-1543). */
    protected readonly currentUserFailed = signal(false);

    /** Read from the environment, not FeedbackManagerService, so the widget's services stay in its deferred chunk. */
    protected readonly feedbackEnabled = !!environment.feedbackFlowUrl;

    private SMALL_DESKTOP_MAX_WIDTH = 1919;
    private SMALL_DESKTOP_MIN_WIDTH = 1440;

    ngOnInit() {
        if (this.route.data) {
            this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(next => this.isPda.set(!!next['pda']));
        }

        this.getComponentData();

        this.deviceManager.windowResized
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(windowSize => {
                this.isSmallDesktop.set(windowSize.width <= this.SMALL_DESKTOP_MAX_WIDTH && windowSize.width >= this.SMALL_DESKTOP_MIN_WIDTH);
            });
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private getComponentData() {
        this.userProfileManager.getCurrentUserAccount()
            .subscribe({
                next: (user: ICentralUserInfo) => {
                    this.currentUser.set(user);
                },
                error: err => {
                    this.currentUserFailed.set(true);
                    this.errorHandler.handleError(err);
                }
            });

    }
}
